import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useAuth } from './AuthContext';
import { getDefaultDates } from '../utils/weekDates';

const SIZE_ORDER = ['mini', 'small', 'medium', 'large'];

// Some legacy rows carry a control character instead of an empty size.
const stripControlChars = value => [...(value || '')].filter(c => c >= ' ').join('');
const cleanSize = size => stripControlChars(size).trim();

const sizeRank = size => {
  const index = SIZE_ORDER.indexOf(cleanSize(size).toLowerCase());
  return index === -1 ? SIZE_ORDER.length : index;
};

const sortKey = value => stripControlChars(value).trim().replace(/\s+/g, ' ').toLowerCase();

export const productLabel = row =>
  [cleanSize(row.size), stripControlChars(row.foodName).trim()].filter(Boolean).join(' ');

// The backend groups on the raw food_name/size text, so the same product can come
// back as two rows when its name differs only by spacing or capitalization. They
// are one thing to bake, so their quantities have to be added together.
const mergeDuplicates = rows => {
  const merged = new Map();
  rows.forEach(row => {
    const key = `${sortKey(row.foodName)}|${sortKey(row.size)}`;
    const existing = merged.get(key);
    const quantity = Number(row.totalQuantity) || 0;
    if (existing) {
      existing.totalQuantity += quantity;
    } else {
      merged.set(key, { ...row, totalQuantity: quantity });
    }
  });
  return [...merged.values()];
};

export const groupByCategory = rows => {
  const groups = new Map();
  rows.forEach(row => {
    const category = stripControlChars(row.category).trim() || 'Uncategorized';
    if (!groups.has(category)) groups.set(category, []);
    groups.get(category).push(row);
  });

  return [...groups.entries()]
    .sort(([a], [b]) => {
      if (a === 'Uncategorized') return 1;
      if (b === 'Uncategorized') return -1;
      return a.localeCompare(b);
    })
    .map(([category, items]) => ({
      category,
      items: mergeDuplicates(items).sort((a, b) =>
        sortKey(a.foodName).localeCompare(sortKey(b.foodName)) ||
        sizeRank(a.size) - sizeRank(b.size) ||
        sortKey(a.size).localeCompare(sortKey(b.size))
      ),
    }));
};

const ProductQuantityNeeded = () => {
  const defaults = getDefaultDates();
  const [menuTypes, setMenuTypes] = useState([]);
  const [selectedMenuType, setSelectedMenuType] = useState('');
  const [pendingStart, setPendingStart] = useState(defaults.start);
  const [pendingEnd, setPendingEnd] = useState(defaults.end);
  const [productQuantities, setProductQuantities] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const latestRequest = useRef(0);
  const { token } = useAuth();

  const authHeaders = { 'Authorization': `Bearer ${token}`, 'Content-Type': 'application/json' };

  const fetchTotals = async (start, end, menuType) => {
    // Responses can land out of order, and showing quantities from a window the
    // owner has already moved on from is worse than showing nothing.
    const requestId = latestRequest.current + 1;
    latestRequest.current = requestId;
    setLoading(true);
    setError(null);
    try {
      const params = new URLSearchParams({ startDate: start, endDate: end });
      if (menuType) params.set('menuType', menuType);
      const res = await fetch(
        `https://api.reshipesbakery.com/reshipes/getThisWeeksFoodTotals?${params}`,
        { headers: authHeaders }
      );
      if (!res.ok) throw new Error('Failed to load product quantities');
      const data = await res.json();
      if (requestId !== latestRequest.current) return;
      setProductQuantities(data);
    } catch (e) {
      if (requestId !== latestRequest.current) return;
      setError(e.message);
      // Drop the old numbers: they belong to a different query.
      setProductQuantities(null);
    } finally {
      if (requestId === latestRequest.current) setLoading(false);
    }
  };

  useEffect(() => {
    fetch('https://api.reshipesbakery.com/reshipes/getMenuTypes', { headers: authHeaders })
      .then(r => r.ok ? r.json() : []).then(setMenuTypes).catch(() => {});
    fetchTotals(defaults.start, defaults.end, '');
  }, [token]);

  const handleApply = () => fetchTotals(pendingStart, pendingEnd, selectedMenuType);

  const groupedQuantities = useMemo(
    () => groupByCategory(productQuantities || []),
    [productQuantities]
  );

  return (
    <div className="container mt-5">
      <h1>Product Quantities Needed</h1>

      <div className="mt-3 d-flex flex-wrap gap-3 align-items-end">
        <div>
          <label className="form-label mb-1" style={{ fontSize: '0.85rem', fontWeight: 600 }}>Menu</label>
          <select
            className="form-select form-select-sm"
            value={selectedMenuType}
            onChange={e => setSelectedMenuType(e.target.value)}
            style={{ minWidth: 160 }}
          >
            <option value="">All Menus</option>
            {menuTypes.map(m => <option key={m} value={m}>{m}</option>)}
          </select>
        </div>
        <div>
          <label className="form-label mb-1" style={{ fontSize: '0.85rem', fontWeight: 600 }}>From</label>
          <input type="date" className="form-control form-control-sm"
            value={pendingStart} onChange={e => setPendingStart(e.target.value)} />
        </div>
        <div>
          <label className="form-label mb-1" style={{ fontSize: '0.85rem', fontWeight: 600 }}>To</label>
          <input type="date" className="form-control form-control-sm"
            value={pendingEnd} onChange={e => setPendingEnd(e.target.value)} />
        </div>
        <button className="btn btn-primary btn-sm" onClick={handleApply}>Apply</button>
      </div>

      {loading && <div className="mt-4">Loading…</div>}
      {error && <div className="mt-4 text-danger">{error}</div>}

      {!loading && productQuantities && productQuantities.length === 0 && (
        <p className="mt-4">No orders found for this period.</p>
      )}

      {!loading && productQuantities && productQuantities.length > 0 && (
        <div className="mt-4">
          {groupedQuantities.map(({ category, items }, categoryIndex) => (
            <section
              key={category}
              className={categoryIndex > 0 ? 'mt-5 pt-4 border-top' : ''}
            >
              <h2 className="h4 mb-3">{category}</h2>
              <table className="table table-striped">
                {/* Fixed widths so every category's table lines up with the others. */}
                <colgroup>
                  <col style={{ width: '70%' }} />
                  <col style={{ width: '30%' }} />
                </colgroup>
                <thead>
                  <tr>
                    <th>Product Name</th>
                    <th>Quantity Needed</th>
                  </tr>
                </thead>
                <tbody>
                  {items.map((product, index) => (
                    <tr key={`${productLabel(product)}-${index}`}>
                      <td>{productLabel(product)}</td>
                      <td>{product.totalQuantity}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </section>
          ))}
        </div>
      )}
    </div>
  );
};

export default ProductQuantityNeeded;
